import Hls, { Level } from "hls.js";
import { I18n } from "@assets/strings";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { SyncLoader } from "react-spinners";
import styled, { css } from "styled-components";
import { useAssetPolling } from "@domain/media/hooks";
import { Media } from "@domain/media/media";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { CameraIcon, ErrorIcon } from "@ui/components/icons";
import { stack } from "@ui/components/layout";
import { KebabMenu } from "@ui/components/menus/kebabMenu";
import { MenuRow } from "@ui/components/menus/menuRow";

interface TooshareVideoPlayerProps extends React.HTMLAttributes<HTMLDivElement> {
	asset: Media;
	onAssetUpdated?: (asset: Media) => void;
	disablePictureInPicture?: boolean;
	autoPlay?: boolean;
}
export const TooshareVideoPlayer: React.FC<TooshareVideoPlayerProps> = ({
	asset,
	onAssetUpdated,
	disablePictureInPicture,
	autoPlay,
	...htmlProps
}) => {
	const videoRef = useRef<HTMLVideoElement>(null);
	const [qualityLevels, setQualityLevels] = useState<Level[]>([]);
	const hls = useMemo(() => new Hls(), []);
	const [quality, setQuality] = useState(-1);
	const { result: updatedAsset } = useAssetPolling(asset.id);
	const usedAsset = updatedAsset ?? asset;
	const assetHlsUrl = usedAsset?.metadata?.playlist ? `${usedAsset?.url}${usedAsset?.metadata?.playlist}` : null;

	const [isVideoVisible, setVideoVisible] = useState(false);
	const pausedByUserRef = useRef(false);
	useEffect(() => {
		if (updatedAsset) {
			onAssetUpdated?.(updatedAsset);
		}
	}, [updatedAsset]);

	useEffect(() => {
		if (!videoRef.current) {
			return;
		}
		if (!Hls.isSupported() && assetHlsUrl) {
			videoRef.current.src = assetHlsUrl + "#t=1";
			return;
		}
		// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
		const handleIntersection = (entries: IntersectionObserverEntry[]) => {
			const [entry] = entries;
			setVideoVisible(entry.isIntersecting);
		};

		const observer = new IntersectionObserver(handleIntersection, {
			threshold: 0.5,
		});
		observer.observe(videoRef.current);
		return () => {
			observer.disconnect();
		};
	}, [assetHlsUrl]);

	useEffect(() => {
		if (isVideoVisible) {
			if (!pausedByUserRef.current) {
				videoRef.current?.play();
			}
		} else {
			videoRef.current?.pause();
		}
	}, [isVideoVisible]);

	useEffect(() => {
		hls.config.startLevel = -1;
		hls.on(Hls.Events.MEDIA_ATTACHED, () => {
			if (assetHlsUrl) {
				hls.loadSource(assetHlsUrl);
			}
		});
		hls.on(Hls.Events.MANIFEST_PARSED, (_e, data) => {
			setQualityLevels(data.levels);
		});
		if (assetHlsUrl && videoRef.current) {
			hls.attachMedia(videoRef.current);
		}
	}, [assetHlsUrl]);

	return (
		<Container {...htmlProps}>
			{assetHlsUrl ? (
				<video
					ref={videoRef}
					controls
					disablePictureInPicture={disablePictureInPicture}
					autoPlay={autoPlay}
					muted={true}
					onClick={(e) => e.stopPropagation()}
					onPause={() => {
						if (isVideoVisible) {
							pausedByUserRef.current = true;
						}
					}}
					onPlaying={() => {
						pausedByUserRef.current = false;
					}}
					src={assetHlsUrl}
				></video>
			) : (
				<VideoPlaceholder>
					<div>
						<IconWrapper>{asset.metadata?.status === "ON_ERROR" ? <ErrorIcon /> : <CameraIcon />}</IconWrapper>
						<PrimaryMedium>
							{asset.metadata?.status === "ON_ERROR"
								? I18n.t("video.transcoding.error")
								: I18n.t("video.transcoding.progress", { progress: asset.metadata?.progress ?? 0 })}
						</PrimaryMedium>
						{asset.metadata?.status === "IN_PROGRESS" && <SyncLoader size={10} />}
					</div>
				</VideoPlaceholder>
			)}
			{qualityLevels.length > 0 && (
				<QualityController
					className="quality-control"
					blur
					bottomSheetMode
					menuContentStyle={{ padding: 0 }}
					closeOnSelect
				>
					<QualityRow
						onClick={() => {
							if (quality !== -1) {
								setQuality(-1);
								hls.currentLevel = -1;
							}
						}}
						current={quality === -1}
					>
						{I18n.t("video.quality.auto")}
					</QualityRow>
					{qualityLevels.map((level, i) => (
						<QualityRow
							key={i}
							onClick={() => {
								if (quality !== i) {
									setQuality(i);
									if (hls.currentLevel !== i) {
										hls.currentLevel = i;
									}
								}
							}}
							current={quality === i}
						>{`${level.height}p`}</QualityRow>
					))}
				</QualityController>
			)}
		</Container>
	);
};

const Container = styled.div`
	position: relative;
	video {
		max-height: 100vh;
		max-width: 100vw;
		height: 100%;
		width: 100%;
		object-fit: cover;
	}
`;

const QualityController = styled(KebabMenu)`
	position: absolute;
	right: 10px;
	top: 10px;
`;

const QualityRow = styled(MenuRow)<{ current?: boolean }>`
	padding: 10px 20px;
	${({ current, theme }) =>
		current &&
		css`
			background-color: ${theme.colors.secondaryLight};
			color: ${theme.colors.secondary};
		`};
`;

const VideoPlaceholder = styled.div`
	width: 100%;
	padding-top: 56.25%;
	background-color: ${({ theme }) => theme.colors.bodyBackground};
	border-radius: 7px;
	min-height: 250px;
	padding: 20px;

	> div {
		position: absolute;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		padding: 30px 40px 20px;
		${stack(15, "center", "center")};
		text-align: center;
	}
`;

const IconWrapper = styled.div`
	background-color: ${({ theme }) => theme.colors.white};
	border-radius: 50%;
	padding: 16px;
	> svg {
		width: 32px;
		height: 32px;
	}
`;
